import {HTHGameConfig} from "../HTHGameConfig";
import {HTH} from "../HTH";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {gsap} from "gsap";
import {HTHHelper} from "../utils/HTHHelper";
import {HTHSound} from "../sound/HTHSound";

export class ScatterCollector extends PIXI.Container {

    private _scatterCollectorSpineVertical: PIXI.spine.Spine;
    private _scatterCollectorSpineHorizontal: PIXI.spine.Spine;

    constructor() {
        super();
        this.name = "ScatterCollectorView";
        this.position = HTHGameConfig.reelAreaPos;
        this.load();
    }

    private _scatterCount: number;

    get scatterCount(): number {
        return this._scatterCount;
    }

    set scatterCount(value: number) {
        this._scatterCount = value;
    }

    public onResizeSummoned(): void {
        if (NolimitApplication.isLandscape) {
            this._scatterCollectorSpineVertical.visible = true;
            this._scatterCollectorSpineHorizontal.visible = false;
        } else {
            this._scatterCollectorSpineVertical.visible = false;
            this._scatterCollectorSpineHorizontal.visible = true;
        }
    }

    public showScatterCollector(toShow: boolean): gsap.core.Timeline {
        const showTl: gsap.core.Timeline = gsap.timeline();
        showTl.to(this, {alpha: toShow ? 1 : 0, duration: 0.25});
            this._scatterCollectorSpineVertical.state.setAnimation(0, "vertical_off", false);
            this._scatterCollectorSpineHorizontal.state.setAnimation(0, "horizontal_off", false);
            this.visible = toShow;
            this._scatterCount = HTH.model.data.scatterCountFS % 3;
            this._scatterCollectorSpineVertical.autoUpdate = true;
            this._scatterCollectorSpineHorizontal.autoUpdate = true;
            if(!toShow){
                HTHHelper.resetSpineItem(this._scatterCollectorSpineVertical);
                HTHHelper.resetSpineItem(this._scatterCollectorSpineHorizontal);
            }


        return showTl;
    }

    public animateScatterCollector(target: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (target !== 0) {
            tl.add(() => {
                this._scatterCollectorSpineVertical.state.setAnimation(0, `vertical_on${target}`, false);
                this._scatterCollectorSpineVertical.state.addAnimation(0, `vertical_on${target}_loop`, true, this._scatterCollectorSpineVertical.skeleton.data.findAnimation(`vertical_on${target}`)?.duration);
                this._scatterCollectorSpineHorizontal.state.setAnimation(0, `horizontal_on${target}`, false);
                this._scatterCollectorSpineHorizontal.state.addAnimation(0, `horizontal_on${target}_loop`, true, this._scatterCollectorSpineHorizontal.skeleton.data.findAnimation(`horizontal_on${target}`)?.duration);
                HTHSound.playTrack(HTHSound.SCATTER_METER_ADD_LIGHT);
            }, 0)

            tl.to(this, {duration: this._scatterCollectorSpineVertical.skeleton.data.findAnimation(`vertical_on${target}_loop`)?.duration});

        } else {
            tl.add(() => {
                this._scatterCollectorSpineVertical.state.setAnimation(0, `vertical_on3`, false);
                this._scatterCollectorSpineVertical.state.addAnimation(0, `vertical_on3_loop`, true, this._scatterCollectorSpineVertical.skeleton.data.findAnimation(`vertical_on3`)?.duration);
                this._scatterCollectorSpineHorizontal.state.setAnimation(0, `horizontal_on3`, false);
                this._scatterCollectorSpineHorizontal.state.addAnimation(0, `horizontal_on3_loop`, true, this._scatterCollectorSpineHorizontal.skeleton.data.findAnimation(`horizontal_on3`)?.duration);
                HTHSound.playTrack(HTHSound.SCATTER_METER_ADD_LIGHT);
            }, 0)
            tl.to(this, {duration: this._scatterCollectorSpineVertical.skeleton.data.findAnimation(`vertical_on3_loop`)?.duration});

            if (!HTH.model.isRestore) {
                tl.add(HTH.gameViewContainer.shakeEffect(0.2, 50, false, 10));
                tl.add(HTH.fsGainedView.showSpinAndSafeWonImage());
                tl.add(() => {
                    HTHSound.playTrack(HTHSound.PLUS_TWO_SPINS_UI);
                }, "<");
            }
            tl.add(this.resetCollectorLights());
        }
        return tl;
    }

    public restoreCollectorLight(value: number): void {
        if (value !== 0) {
            this._scatterCollectorSpineVertical.state.setAnimation(0, `vertical_on${value}_loop`, true);
            this._scatterCollectorSpineHorizontal.state.setAnimation(0, `horizontal_on${value}_loop`, true);
        } else {
            this._scatterCollectorSpineVertical.state.setAnimation(0, `vertical_reset`, false);
            this._scatterCollectorSpineHorizontal.state.setAnimation(0, `horizontal_reset`, false);
        }
    }

    public resetCollectorLights(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this._scatterCollectorSpineVertical.state.setAnimation(0, `vertical_reset`, false);
            this._scatterCollectorSpineHorizontal.state.setAnimation(0, `horizontal_reset`, false);
        }, 0)
        tl.to(this, {duration: this._scatterCollectorSpineVertical.skeleton.data.findAnimation(`horizontal_reset`)?.duration});
        return tl;
    }

    public playPumpAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this._scatterCollectorSpineVertical.state.setAnimation(0, "vertical_FStease", true);
            this._scatterCollectorSpineHorizontal.state.setAnimation(0, "horizontal_FStease", true);
        })
        tl.to(this, {duration: this._scatterCollectorSpineVertical.skeleton.data.findAnimation(`vertical_FStease`)?.duration});
        return tl;

    }

    public resetScatterCollectorSize(): void {
        this._scatterCollectorSpineVertical.state.setAnimation(0, `vertical_on2_loop`, true);
        this._scatterCollectorSpineHorizontal.state.setAnimation(0, `horizontal_on2_loop`, true);
    }

    private load(): void {

        this._scatterCollectorSpineVertical = new PIXI.spine.Spine(HTH.assetLoader.getSpineAsset("FS_meter"));
        this._scatterCollectorSpineHorizontal = new PIXI.spine.Spine(HTH.assetLoader.getSpineAsset("FS_meter"));
        this._scatterCollectorSpineVertical.state.setAnimation(0, "vertical_off", false);
        this._scatterCollectorSpineHorizontal.state.setAnimation(0, "horizontal_off", false);

        this._scatterCollectorSpineHorizontal.position.set(499, -162);
        this._scatterCollectorSpineVertical.position.set(742, 66);

        this.addChild(this._scatterCollectorSpineVertical, this._scatterCollectorSpineHorizontal);
        this._scatterCollectorSpineVertical.name = "scatterBoxVertical";
        this._scatterCollectorSpineHorizontal.name = "scatterBoxHorizontal";

        if (NolimitApplication.isLandscape) {
            this._scatterCollectorSpineHorizontal.visible = false;
        } else {
            this._scatterCollectorSpineVertical.visible = false;
        }

        this.showScatterCollector(false);

    }

    private animationChange(spine: PIXI.spine.Spine, animName: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(HTHHelper.playSpineWithTimeline(spine, 0, animName));
        return tl;
    }


}